import { useAgent } from "@watermarkinsights/ai-toolbox";
import aiChatIconsSprite from "./AiChatIcons.svg";
import type { MessagePair } from "./draft-message/DraftConversationPanel";

// ── Types ──────────────────────────────────────────────────────────────────────

export type AgentMessage = ReturnType<typeof useAgent>["messages"][number];

export type BarChartDataItem = {
  barLegend: string;
  amount: number;
  groupName?: string;
  popoverTitle?: string;
  popoverText?: string;
};

export type BarChartPayload = {
  chartType: "bar";
  label?: string;
  valueAxisLabel?: string;
  valueFormat?: "amount" | "percentage" | "none";
  sort?: "ascending" | "descending" | "none";
  data: BarChartDataItem[];
};

export type PieChartDataItem = {
  legend: string;
  amount: number;
};

export type PieChartPayload = {
  chartType: "pie";
  label?: string;
  valueFormat?: "amount" | "percentage" | "none";
  data: PieChartDataItem[];
};

export type LineChartDataPoint = {
  x: string;
  y: number | null;
};

export type LineChartSeries = {
  label: string;
  data: LineChartDataPoint[];
};

export type LineChartPayload = {
  chartType: "line";
  label?: string;
  xAxisLabel?: string;
  yAxisLabel?: string;
  units?: string;
  showGrid?: boolean;
  visibilityToggles?: boolean;
  showDeltas?: boolean;
  data: LineChartSeries[];
};

export type SuggestedQuestionsPayload = {
  toolType: "suggestedQuestions";
  label?: string;
  questions: string[];
};

// ── Utilities ──────────────────────────────────────────────────────────────────

type AssistantToolCalls = Extract<
  AgentMessage,
  { role: "assistant" }
>["toolCalls"];

export const buildSvgIconHref = (iconId: string): string =>
  `${aiChatIconsSprite}#${iconId}`;

export const extractTextFromMessageContent = (
  content: AgentMessage["content"],
): string => {
  if (typeof content === "string") return content;
  if (Array.isArray(content)) {
    return content
      .map((block) => (block.type === "text" ? block.text : `[${block.type}]`))
      .join(" ");
  }
  return "";
};

export const extractToolNameFromAssistantMessage = (
  message: AgentMessage,
): string | null => {
  if (message.role !== "assistant") return null;
  const assistantMessage = message as Extract<
    AgentMessage,
    { role: "assistant" }
  >;
  if (extractTextFromMessageContent(assistantMessage.content).trim())
    return null;
  return assistantMessage.toolCalls?.[0]?.function?.name ?? null;
};

export const extractFirstMatchingToolCallPayload = <T>(
  toolCalls: AssistantToolCalls,
  parser: (args: string) => T | null,
): T | null =>
  toolCalls?.reduce<T | null>((found, toolCall) => {
    if (found) return found;
    return toolCall.function?.arguments
      ? parser(toolCall.function.arguments)
      : null;
  }, null) ?? null;

export const parseBarChartFromToolArgs = (
  args: string,
): BarChartPayload | null => {
  try {
    const parsed = JSON.parse(args);
    if (
      parsed.chartType === "bar" &&
      Array.isArray(parsed.data) &&
      parsed.data.length > 0
    ) {
      return parsed as BarChartPayload;
    }
  } catch {}
  return null;
};

export const parsePieChartFromToolArgs = (
  args: string,
): PieChartPayload | null => {
  try {
    const parsed = JSON.parse(args);
    if (
      parsed.chartType === "pie" &&
      Array.isArray(parsed.data) &&
      parsed.data.length > 0
    ) {
      return parsed as PieChartPayload;
    }
  } catch {}
  return null;
};

export const parseLineChartFromToolArgs = (
  args: string,
): LineChartPayload | null => {
  try {
    const parsed = JSON.parse(args);
    if (
      parsed.chartType === "line" &&
      Array.isArray(parsed.data) &&
      parsed.data.length > 0
    ) {
      return parsed as LineChartPayload;
    }
  } catch {}
  return null;
};

export const parseSuggestedQuestionsFromToolArgs = (
  args: string,
): SuggestedQuestionsPayload | null => {
  try {
    const parsed = JSON.parse(args);
    if (
      parsed.toolType === "suggestedQuestions" &&
      Array.isArray(parsed.questions) &&
      parsed.questions.length > 0
    ) {
      return parsed as SuggestedQuestionsPayload;
    }
  } catch {}
  return null;
};

export type TablePayload = {
  toolType: "renderTable";
  columns: string[];
  rows: (string | number)[][];
  title?: string;
};

export const parseTableFromToolArgs = (args: string): TablePayload | null => {
  try {
    const parsed = JSON.parse(args);
    if (
      parsed.toolType === "renderTable" &&
      Array.isArray(parsed.columns) &&
      parsed.columns.length > 0 &&
      Array.isArray(parsed.rows) &&
      parsed.rows.length > 0
    ) {
      return parsed as TablePayload;
    }
  } catch {}
  return null;
};

// ── Message list derivation ─────────────────────────────────────────────────────

export const deriveVisibleMessages = (
  messages: AgentMessage[],
  clearedMessageCount: number,
): AgentMessage[] =>
  messages
    .slice(clearedMessageCount)
    .filter((message) => message.role === "user" || message.role === "assistant")
    .filter((message, index, allMessages) => {
      const toolName = extractToolNameFromAssistantMessage(message);
      return (
        !toolName ||
        extractToolNameFromAssistantMessage(allMessages[index - 1]) !== toolName
      );
    });

export const buildMessagePairs = (
  visibleMessages: AgentMessage[],
): MessagePair[] =>
  visibleMessages.reduce<MessagePair[]>((messagePairs, message, index) => {
    if (message.role !== "assistant") return messagePairs;
    const response = extractTextFromMessageContent(message.content).trim();
    if (!response) return messagePairs;
    const previousMessage = visibleMessages[index - 1];
    const question =
      previousMessage?.role === "user"
        ? extractTextFromMessageContent(previousMessage.content)
        : "";
    messagePairs.push({ id: message.id, question, response });
    return messagePairs;
  }, []);
